import {
  Box,
  Icon,
  Image,
  Theme,
  Typography,
  useTheme
} from "@outschool/backpack";
import {
  OSSubscriptions,
  centsPerLearnerPerMeeting
} from "@outschool/business-rules";
import * as FilestackUrls from "@outschool/filestack-urls";
import { ActivityListing_ListingFragment } from "@outschool/gql-frontend-generated";
import { faBirthdayCake, faCalendar, faUsersClass } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { addParamsToUrl } from "@outschool/routes";
import { measurePerformance, useProductClicked } from "@outschool/ui-analytics";
import { CreditIcon, UserHeadshotImage } from "@outschool/ui-components-shared";
import {
  LeaderDetailsListing,
  LeaderDetailsMobileListing,
  SectionSearchFilters,
  isOneOnOne,
  useActivityString,
  useIsActivitySavedByCurrentUser,
  usePriceDetailsString
} from "@outschool/ui-components-website";
import { TrackingProperties } from "@outschool/ui-legacy-component-library";
import {
  useDevicePixelRatio,
  useLinkComponent,
  useNavigation
} from "@outschool/ui-utils";
import React from "react";

import { SaveHeart } from "../saving/SaveButton";
import { ActivityListingProps } from "./ActivityListing";
import { NextStartTime } from "./NextStartTime";

function FundingBadge() {
  const { t } = useTranslation("client\\components\\listings\\MobileListing");

  return (
    <Box
      sx={{
        backgroundColor: "grey.100",
        borderRadius: "3px",
        padding: "2px"
      }}
    >
      <Typography
        variant="subtitle2"
        emphasized
        sx={(theme: Theme) => ({
          color: "grey.700",
          lineHeight: "1",
          [theme.breakpoints.up("md")]: {
            fontSize: "1.3rem"
          }
        })}
      >
        {t("Funding Accepted")}
      </Typography>
    </Box>
  );
}

function trackingObject(
  activity_uid: string,
  insertionId: string | null | undefined,
  trackingProperties: Record<any, any>
) {
  return {
    trackingName: "Activity Impression",
    sharedProperties: {
      activity_uid,
      // Only include insertion_id when non-nullish to satisfy LinkProps type constraints
      ...(insertionId && { insertion_id: insertionId }),
      ...trackingProperties
    },
    trackingTouchEventName: "Activity Impression Click",
    trackingViewEventName: "Activity Impression",
    trackingUniqueId: activity_uid
  };
}

type MobileListingProps = {
  activity: ActivityListing_ListingFragment["activity"];
  nextSection: ActivityListing_ListingFragment["nextSection"];
  leader: ActivityListing_ListingFragment["leader"];
  sectionSearchFilters?: SectionSearchFilters;
  trackingProperties: TrackingProperties;
  disabled: boolean;
  insertionId?: ActivityListing_ListingFragment["insertionId"];
  listingIndex?: number;
  listingDetailsOverride?: ActivityListingProps["listingDetailsOverride"];
  showCredits?: boolean;
  openInNewTab?: boolean;
  showFundingBadge?: boolean;
};

export const MobileListing = ({
  activity,
  nextSection,
  leader,
  sectionSearchFilters,
  trackingProperties,
  disabled,
  insertionId,
  listingIndex = -1,
  listingDetailsOverride,
  showCredits,
  openInNewTab,
  showFundingBadge = false
}: MobileListingProps) => {
  const Link = useLinkComponent();
  const theme = useTheme();
  const trackProductClicked = useProductClicked();

  // @ts-ignore TS(2339): Property 'path' does not exist on type '{ __typena... Remove this comment to see the full error message
  let activityPath = activity.path;

  if (!!sectionSearchFilters) {
    activityPath = addParamsToUrl(activityPath, sectionSearchFilters);
  }

  const navigate = useNavigation();

  const onLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    trackProductClicked({ activity, leader }, listingIndex);

    if (openInNewTab) {
      e.preventDefault();
      navigate(activityPath, { newTab: true, hardNav: true });
    }
  };

  const { ageRangeString } = useActivityString();

  const isSaved = useIsActivitySavedByCurrentUser(activity);
  const pixelRatio = useDevicePixelRatio();
  const photo = FilestackUrls.imageUrl(activity.details.photo, {
    height: 90 * pixelRatio,
    width: 120 * pixelRatio,
    fit: "crop"
  });

  const {
    perClassString,
    classTypeString,
    perMeetingPriceString,
    detailedScheduleString
  } = usePriceDetailsString();

  // TODO: Refactor to reduce duplication of layout styling
  if (listingDetailsOverride) {
    return (
      <>
        <Box
          flex
          id={activity.uid}
          sx={{
            borderBottom: "1px solid",
            borderColor: "grey.200",

            flexDirection: "column"
          }}
        >
          <Link
            to={activityPath}
            {...trackingObject(activity?.uid, insertionId, trackingProperties)}
            useLinkTracking
            tabIndex={disabled ? -1 : undefined}
            onClick={onLinkClick}
            style={{ textDecoration: "none", color: "black" }}
            trackViews={true}
          >
            <Box
              flex
              sx={{
                flexDirection: "column",
                padding: "8px",
                gap: "8px"
              }}
            >
              <Box
                flex
                sx={{
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8
                }}
              >
                <Box sx={{ maxWidth: "calc(60vw - 40px)" }}>
                  <Typography
                    variant="subtitle2"
                    sx={(theme: Theme) => ({
                      [theme.breakpoints.up("md")]: {
                        // Override the s2 variant fontSize at md breakpoint
                        fontSize: "1.3rem"
                      }
                    })}
                  >
                    {activity.titleTranslated}
                  </Typography>

                  <LeaderDetailsMobileListing leader={leader} />
                </Box>

                <Box
                  flex
                  sx={{
                    position: "relative"
                  }}
                >
                  <Image
                    sx={theme => ({
                      borderRadius: "12px",
                      objectFit: "contain",
                      display: "block",
                      [theme.breakpoints.down("sm")]: {
                        width: "calc(40vw - 40px)"
                      }
                    })}
                    src={photo}
                    // @ts-ignore TS(2322): Type '(() => void) | null' is not assignable to ty... Remove this comment to see the full error message
                    onLoad={
                      listingIndex === 0
                        ? () => {
                            measurePerformance("os-page-load", {
                              source: "image"
                            });
                          }
                        : null
                    }
                  />
                  <SaveHeart
                    isSet={isSaved}
                    activity={activity}
                    leader={leader}
                    unfilledColor="rgba(255,255,255,.8)"
                    sx={{
                      fontSize: "24px",
                      position: "absolute",
                      top: "6px",
                      right: "6px"
                    }}
                    twoTone
                    disabled={disabled}
                  />
                </Box>
              </Box>
            </Box>
          </Link>
          {listingDetailsOverride}
        </Box>
      </>
    );
  } else {
    return (
      <Link
        to={activityPath}
        {...trackingObject(activity?.uid, insertionId, trackingProperties)}
        useLinkTracking
        tabIndex={disabled ? -1 : undefined}
        onClick={onLinkClick}
        style={{ textDecoration: "none" }}
        trackViews={true}
        data-test-id="activity-listing-link"
      >
        <Box
          flex
          id={activity.uid}
          sx={{
            borderBottom: "1px solid",
            borderColor: "grey.200",
            color: "grey.900",
            flexDirection: "column"
          }}
        >
          <Box
            flex
            sx={{
              flexDirection: "column",
              padding: "8px",
              gap: "8px"
            }}
          >
            <Box
              flex
              sx={{
                alignItems: "center",
                justifyContent: "space-between"
              }}
            >
              <Box
                flex
                sx={{
                  flex: 1,
                  flexDirection: "row",
                  alignItems: "center",
                  gap: "8px"
                }}
              >
                {isOneOnOne(activity) ? (
                  <Box
                    flex
                    sx={{
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "2px"
                    }}
                  >
                    <UserHeadshotImage
                      user={leader}
                      size={48}
                      sx={{ border: "1px solid", borderColor: "gray200" }}
                    />
                  </Box>
                ) : (
                  <Box
                    flex
                    sx={{
                      position: "relative",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "2px"
                    }}
                  >
                    <Image
                      width="48px"
                      height="48px"
                      sx={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "12px",
                        objectFit: "cover",
                        display: "block"
                      }}
                      src={photo}
                      // @ts-ignore TS(2322): Type '(() => void) | null' is not assignable to ty... Remove this comment to see the full error message
                      onLoad={
                        listingIndex === 0
                          ? () => {
                              measurePerformance("os-page-load", {
                                source: "image"
                              });
                            }
                          : null
                      }
                    />
                  </Box>
                )}
                <Box flex sx={{ flex: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={(theme: Theme) => ({
                      [theme.breakpoints.up("md")]: {
                        // Override the s2 variant fontSize at md breakpoint
                        fontSize: "1.3rem"
                      }
                    })}
                  >
                    {activity.titleTranslated}
                  </Typography>
                </Box>
                <SaveHeart
                  isSet={isSaved}
                  activity={activity}
                  leader={leader}
                  unfilledColor="rgba(255,255,255,.8)"
                  sx={{ fontSize: "24px" }}
                  twoTone
                  disabled={disabled}
                />
              </Box>
            </Box>
            <Box flex sx={{ justifyContent: "space-between" }}>
              <LeaderDetailsListing leader={leader} />
              <Box
                flex
                sx={{
                  minWidth: "72px",
                  flexGrow: 1,
                  alignItems: "flex-end",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  gap: "4px"
                }}
              >
                {showFundingBadge && <FundingBadge />}
                <Box
                  flex
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "8px"
                  }}
                >
                  <Typography variant="body2" emphasized color={"grey.900"}>
                    {showCredits ? (
                      <Box
                        sx={{
                          display: "flex",
                          marginTop: "1rem"
                        }}
                      >
                        <Box
                          sx={{
                            paddingRight: "0.5rem"
                          }}
                        >
                          <CreditIcon
                            fillColor={theme.palette.warning.main}
                            strokeColor={theme.palette.warning[700] as string}
                            sx={{ height: "2.4rem", width: "2.4rem" }}
                          />
                        </Box>
                        {OSSubscriptions.priceCentsToCredits(
                          centsPerLearnerPerMeeting(activity)
                        )}
                      </Box>
                    ) : (
                      perMeetingPriceString(activity)
                    )}
                  </Typography>
                  <Typography variant="body2" color={"grey.600"}>
                    {showCredits ? "credits " : ""}
                    {perClassString(activity)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              backgroundColor: "grey.50",
              padding: "8px 8px 0 8px"
            }}
          >
            <NextStartTime
              nextSectionStartTime={nextSection?.start_time}
              nextOngoingMeetingStartTime={
                nextSection?.nextOngoingMeeting?.start_time
              }
            />
          </Box>

          <Box
            flex
            sx={{
              backgroundColor: "grey.50",
              color: "grey.600",
              paddingX: 16,
              height: "32px",
              justifyContent: "space-between"
            }}
          >
            <Box flex sx={{ gap: "4px", alignItems: "center" }}>
              <Icon icon={faUsersClass} />
              <Typography variant="caption">
                {classTypeString(activity)}
              </Typography>
            </Box>
            <Box flex sx={{ gap: "4px", alignItems: "center" }}>
              <Icon icon={faCalendar} />
              <Typography variant="caption">
                {detailedScheduleString(activity)}
              </Typography>
            </Box>
            <Box flex sx={{ gap: "4px", alignItems: "center" }}>
              <Icon icon={faBirthdayCake} />
              <Typography variant="caption">
                {ageRangeString(activity, false)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Link>
    );
  }
};
